import React, { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Save, Loader2, User as UserIcon, Building2, FileText,
  Shield, AlertTriangle, Mail, Phone, MapPin, Calendar, Wallet,
  Edit2, RotateCcw, Hash, CheckCircle2, XCircle,
  PlusCircle, MinusCircle, Receipt, ArrowDownLeft, ArrowUpRight,
  CreditCard, MessageCircle, Globe, History, ListChecks, ChevronLeft,
  TrendingUp, Award, Filter, RefreshCw, Coins, Trash2,
} from 'lucide-react';
import { ALL_COUNTRIES } from '../lib/countries';
import { toast } from 'sonner';
import MessengerLinks from './MessengerLinks';
import { CustomerBadges, useNewCustomerDays } from './CustomerBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  user:          { label: 'User',          color: 'emerald', icon: CheckCircle2, hint: 'Verified — can use the file service.' },
  admin:         { label: 'Admin',         color: 'amber',   icon: Shield,       hint: 'System administrator.' },
  not_confirmed: { label: 'Not confirmed', color: 'slate',   icon: Mail,         hint: 'Email verification pending.' },
  access_denied: { label: 'Access denied', color: 'red',     icon: XCircle,      hint: 'Hard-blocked from logging in.' },
};

const TONE = {
  emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  amber:   'bg-amber-100   text-amber-700   border-amber-200',
  slate:   'bg-slate-100   text-slate-700   border-slate-200',
  red:     'bg-red-100     text-red-700     border-red-200',
};

const Field = ({ label, value, onChange, type = 'text', editing, testid, placeholder = '', readOnly = false, options = null }) => (
  <div data-testid={testid ? `${testid}-wrap` : undefined}>
    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">{label}</label>
    {editing && !readOnly ? (
      options ? (
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
        >
          <option value="">{placeholder || '— Select —'}</option>
          {options.map((opt) => (
            <option key={opt.value ?? opt} value={opt.value ?? opt}>{opt.label ?? opt}</option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testid}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
        />
      )
    ) : (
      <p data-testid={testid} className="text-sm text-slate-800 py-2 border-b border-slate-100 min-h-[36px]">
        {value || <span className="text-slate-300 italic">—</span>}
      </p>
    )}
  </div>
);

const Toggle = ({ label, value, onChange, hint, testid, color = 'cyan' }) => (
  <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
    <input
      type="checkbox" checked={!!value}
      onChange={(e) => onChange(e.target.checked)}
      data-testid={testid}
      className={`mt-0.5 w-4 h-4 text-${color}-600 rounded focus:ring-${color}-500`}
    />
    <div className="flex-1">
      <p className="text-sm font-semibold text-slate-800">{label}</p>
      {hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
    </div>
  </label>
);

export default function CustomerProfileDrawer({ token, customerId, onClose, onSaved }) {
  const newCustomerDays = useNewCustomerDays(token);
  const [profile, setProfile] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // Manual balance adjustment form (admin only — separate from profile edit)
  const [adjKind, setAdjKind] = useState('credit');           // 'credit' | 'debit'
  const [adjAmount, setAdjAmount] = useState('');
  const [adjDescription, setAdjDescription] = useState('');
  const [adjGenerateInvoice, setAdjGenerateInvoice] = useState(true);
  const [adjBusy, setAdjBusy] = useState(false);
  const [adjResult, setAdjResult] = useState(null);
  const [adjErr, setAdjErr] = useState('');

  // EVC balance sync — re-polls EVC and persists `users.evc_balance` on the
  // backend so the admin Customers list / customer's own EVC dashboard all
  // see the fresh value without each making their own EVC API round-trip.
  const [evcSyncing, setEvcSyncing] = useState(false);
  const [evcSyncMsg, setEvcSyncMsg] = useState('');

  // Postpaid / "Order without prepayment" — admin permission editor.
  // Form state is separate from `draft` (the generic profile patcher) and
  // is submitted via its own dedicated endpoint that writes an audit log.
  const [ppEnabled, setPpEnabled] = useState(false);
  const [ppLimit, setPpLimit]     = useState('');
  const [ppValidUntil, setPpValidUntil] = useState('');
  const [ppNote, setPpNote]       = useState('');
  const [ppBusy, setPpBusy]       = useState(false);
  const [ppErr, setPpErr]         = useState('');
  const [ppMsg, setPpMsg]         = useState('');

  // Statistics + rating + view switching (profile/login/transactions/stats/rating)
  const [stats, setStats] = useState(null);
  const [rank, setRank] = useState(null);
  const [view, setView] = useState('profile');
  const [loginItems, setLoginItems] = useState([]);
  const [loginStats, setLoginStats] = useState(null);
  const [loginFilter, setLoginFilter] = useState({ ip: '', date_start: '', date_end: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  const [txnItems, setTxnItems] = useState([]);
  const [txnLoading, setTxnLoading] = useState(false);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [routingInfo, setRoutingInfo] = useState(null);  // resolved routing for this customer

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token],
  );

  // Fetches the customer's profile + statistics. Callable from child
  // views (e.g. TransactionsListView after a delete-and-rollback) so
  // the header cards (Balance, Reserved, Recharged) stay in sync with
  // whatever the child mutated.
  const reloadProfile = async () => {
    try {
      const [r, s] = await Promise.all([
        fetch(`${API_URL}/api/admin/customers/${customerId}/profile`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/customers/${customerId}/statistics`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (r.ok) {
        const data = await r.json();
        setProfile(data); setDraft(data);
        setPpEnabled(!!data.allow_postpaid_orders);
        setPpLimit(data.postpaid_credit_limit ? String(data.postpaid_credit_limit) : '');
        setPpValidUntil(data.postpaid_valid_until ? String(data.postpaid_valid_until).slice(0, 10) : '');
        setPpNote(data.postpaid_note || '');
      }
      if (s.ok) setStats(await s.json());
    } catch { /* silent — child views handle their own error toasts */ }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setErr('');
      try {
        const [r, s] = await Promise.all([
          fetch(`${API_URL}/api/admin/customers/${customerId}/profile`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_URL}/api/admin/customers/${customerId}/statistics`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (alive) {
          setProfile(data); setDraft(data);
          // Hydrate the postpaid editor with the current persisted state.
          setPpEnabled(!!data.allow_postpaid_orders);
          setPpLimit(data.postpaid_credit_limit ? String(data.postpaid_credit_limit) : '');
          setPpValidUntil(
            data.postpaid_valid_until
              ? String(data.postpaid_valid_until).slice(0, 10) // YYYY-MM-DD
              : '',
          );
          setPpNote(data.postpaid_note || '');
        }
        if (s.ok) { const sd = await s.json(); if (alive) setStats(sd); }
        // Load customer groups (for the dropdown)
        fetch(`${API_URL}/api/customer-groups/`, { headers: { Authorization: `Bearer ${token}` } })
          .then((g) => g.ok ? g.json() : [])
          .then((gd) => { if (alive) setCustomerGroups(gd || []); })
          .catch(() => {});
        // Resolve current PayPal routing for this customer
        fetch(`${API_URL}/api/payments/admin/resolve-routing/${customerId}`, { headers: { Authorization: `Bearer ${token}` } })
          .then((rr) => rr.ok ? rr.json() : null)
          .then((rd) => { if (alive) setRoutingInfo(rd); })
          .catch(() => {});      } catch (e) {
        if (alive) setErr(e.message || 'Failed to load profile');
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [customerId, token]);

  const loadLoginHistory = async () => {
    setLoginLoading(true);
    try {
      const params = new URLSearchParams({ user_id: customerId, limit: '200' });
      if (loginFilter.ip) params.append('ip', loginFilter.ip);
      if (loginFilter.date_start) params.append('date_start', loginFilter.date_start);
      if (loginFilter.date_end) params.append('date_end', loginFilter.date_end);
      const [h, s] = await Promise.all([
        fetch(`${API_URL}/api/admin/login-history?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/customers/${customerId}/login-stats?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (h.ok) { const d = await h.json(); setLoginItems(d.items || []); }
      if (s.ok) setLoginStats(await s.json());
    } finally { setLoginLoading(false); }
  };

  const toggleBlockLogin = async (event_id) => {
    try {
      const r = await fetch(`${API_URL}/api/admin/login-events/${event_id}/block`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const body = await r.json();
        setLoginItems((rows) => rows.map((x) => x.id === event_id ? { ...x, blocked: body.blocked } : x));
      }
    } catch (_) { /* silent */ }
  };

  const loadTransactions = async () => {
    setTxnLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/transactions/filter?user_id=${customerId}&limit=500`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setTxnItems(await r.json());
    } finally { setTxnLoading(false); }
  };

  const loadRank = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/customers/${customerId}/rating-rank`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setRank(await r.json());
    } catch (_) { /* silent */ }
  };

  // Re-poll EVC for THIS customer and persist `users.evc_balance` on the
  // backend. The endpoint returns the live number so we can patch the
  // drawer's `profile` state immediately for visual feedback, without
  // refetching the whole profile.
  const syncEvcBalance = async () => {
    if (!customerId) return;
    setEvcSyncing(true); setEvcSyncMsg('');
    try {
      const r = await fetch(`${API_URL}/api/evc/admin/sync-balance/${customerId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `Sync failed (${r.status})`);
      setProfile((p) => p ? {
        ...p,
        evc_balance: j.evc_balance,
        evc_balance_synced_at: j.synced_at,
      } : p);
      setEvcSyncMsg(`Synced — balance ${j.evc_balance} credits.`);
    } catch (e) {
      setEvcSyncMsg(`Failed: ${e.message || e}`);
    } finally {
      setEvcSyncing(false);
    }
  };

  // Lazy load when switching views — always refetch transactions when the
  // tab is opened so newly-created orders/refunds appear immediately
  // instead of getting masked by a stale cache from a previous open.
  useEffect(() => {
    if (view === 'login' && loginItems.length === 0 && !loginLoading) loadLoginHistory();
    if (view === 'transactions' && !txnLoading) loadTransactions();
    if (view === 'rating' && !rank) loadRank();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const set = (field, value) => setDraft((d) => ({ ...d, [field]: value }));

  // Trusted flag — instant save, independent of the edit-mode draft.
  const [trustedBusy, setTrustedBusy] = useState(false);
  const toggleTrusted = async () => {
    if (!customerId || !profile) return;
    const next = !profile.trusted;
    if (!next && !window.confirm(`Remove the Trusted flag from ${profile.email}?\n\nThey will fall back to the strict new-client limits.`)) return;
    setTrustedBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/customers/${customerId}/trusted`, {
        method: 'PUT', headers, body: JSON.stringify({ trusted: next }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || `HTTP ${r.status}`);
      setProfile((p) => (p ? { ...p, trusted: next } : p));
      setDraft((d) => (d ? { ...d, trusted: next } : d));
      toast.success(next ? 'Marked as Trusted client' : 'Trusted flag removed');
    } catch (e) {
      toast.error(e.message || 'Could not update the Trusted flag');
    } finally { setTrustedBusy(false); }
  };

  const dirty = useMemo(() => {
    if (!editing || !profile || !draft) return false;
    return Object.keys(draft).some((k) =>
      ['orders_count', 'transactions_count', 'recharged_amount', 'reserved', 'balance', 'created_at', 'updated_at', 'registration_date', 'id'].indexOf(k) === -1
      && profile[k] !== draft[k]);
  }, [editing, profile, draft]);

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const fields = [
        'first_name', 'surname', 'email', 'country', 'phone', 'phone_country',
        'evc_registered', 'evc_customer_id', 'evc_reseller_name',
        'is_company', 'company_name', 'vat', 'address', 'zipcode', 'city',
        'role', 'is_active', 'credit_limit', 'recharge_disabled', 'admin_notes', 'group',
        'risk_balance_cap_eur',
        'price_coefficient', 'customer_group_id', 'forced_company',
        'disabled_priorities',
      ];
      const payload = {};
      fields.forEach((f) => { if (draft && f in draft) payload[f] = draft[f]; });
      const r = await fetch(`${API_URL}/api/admin/customers/${customerId}/profile`, {
        method: 'PUT', headers, body: JSON.stringify(payload),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(body.detail || `Save failed (HTTP ${r.status})`); return; }
      setProfile(body.user); setDraft(body.user); setEditing(false);
      if (onSaved) onSaved();
    } catch (e) {
      setErr(e.message || 'Network error');
    } finally { setSaving(false); }
  };

  const submitAdjustment = async () => {
    setAdjErr(''); setAdjResult(null);
    const amt = parseFloat(adjAmount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setAdjErr('Amount must be a positive number'); return;
    }
    if (!adjDescription.trim()) {
      setAdjErr('Description is required (it is shown in transactions and on the invoice)'); return;
    }
    const action = adjKind === 'credit' ? 'add credit to' : 'deduct from';
    if (!window.confirm(`Confirm: ${action} ${profile?.email}'s balance €${amt.toFixed(2)}?\n\nDescription: ${adjDescription.trim()}${adjKind === 'credit' && adjGenerateInvoice ? '\n\nA PDF invoice will be generated and emailed to the customer.' : ''}`)) {
      return;
    }
    setAdjBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/transactions/admin/adjust-balance`, {
        method: 'POST', headers,
        body: JSON.stringify({
          user_id: customerId,
          amount: amt,
          kind: adjKind,
          description: adjDescription.trim(),
          generate_invoice: adjKind === 'credit' ? !!adjGenerateInvoice : false,
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) { setAdjErr(body.detail || `Failed (HTTP ${r.status})`); return; }
      setAdjResult(body);
      setAdjAmount(''); setAdjDescription('');
      // Refresh profile to show updated balance
      const r2 = await fetch(`${API_URL}/api/admin/customers/${customerId}/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r2.ok) {
        const data = await r2.json();
        setProfile(data);
        if (!editing) setDraft(data);
      }
      if (onSaved) onSaved();
    } catch (e) {
      setAdjErr(e.message || 'Network error');
    } finally { setAdjBusy(false); }
  };

  const submitPostpaid = async () => {
    setPpErr(''); setPpMsg('');
    const limit = parseFloat(ppLimit);
    if (ppEnabled && (!Number.isFinite(limit) || limit <= 0)) {
      setPpErr('A positive Negative balance limit (€) is required to enable postpaid ordering.');
      return;
    }
    if (!window.confirm(
      ppEnabled
        ? `Enable postpaid ordering for ${profile?.email}?\n\nLimit: €${limit.toFixed(2)}${ppValidUntil ? `\nValid until: ${ppValidUntil}` : ''}\n\nThis allows the customer to submit orders even when their wallet is negative.`
        : `Disable postpaid ordering for ${profile?.email}?\n\nFuture orders will be rejected if balance is insufficient.`,
    )) return;
    setPpBusy(true);
    try {
      const body = {
        enabled:     ppEnabled,
        limit:       ppEnabled ? limit : 0,
        valid_until: ppEnabled && ppValidUntil ? ppValidUntil : null,
        note:        ppNote.trim() || null,
      };
      const r = await fetch(`${API_URL}/api/admin/customers/${customerId}/postpaid`, {
        method: 'PUT', headers, body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setPpErr(j.detail || `Failed (HTTP ${r.status})`); return; }
      setPpMsg(j.message || 'Postpaid permission updated');
      // Refresh profile so the read-only summary reflects the new state.
      const r2 = await fetch(`${API_URL}/api/admin/customers/${customerId}/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r2.ok) {
        const data = await r2.json();
        setProfile(data);
        if (!editing) setDraft(data);
      }
      if (onSaved) onSaved();
    } catch (e) {
      setPpErr(e.message || 'Network error');
    } finally { setPpBusy(false); }
  };

  const role = profile?.role || 'user';
  const roleMeta = ROLE_LABELS[role] || ROLE_LABELS.user;
  const RoleIcon = roleMeta.icon;

  return createPortal((
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 md:p-6" data-testid="customer-profile-drawer">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      {/* Centered modal — was a right-side drawer (md:max-w-3xl). The
          new shell takes ~75% of the viewport on desktop so every
          subsection has room to breathe (Profile fields in a 4-col
          grid, Transactions/Login history get extra columns visible
          without horizontal scroll). Falls back to full-width on
          phones. Max-width capped at 1400px so the layout doesn't
          stretch absurdly on ultra-wide monitors. */}
      <div className="relative w-full max-w-[1400px] md:w-[85vw] lg:w-[78vw] xl:w-[72vw] h-[92vh] bg-slate-50 shadow-2xl flex flex-col rounded-2xl overflow-hidden">
        {/* Header — compact on mobile */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white px-3 md:px-6 py-2.5 md:py-4 flex items-center justify-between gap-2 flex-shrink-0">
          <div className="min-w-0 flex-1">
            <h2 className="text-sm md:text-lg font-bold truncate flex items-center gap-1.5 md:gap-2">
              {profile ? `${profile.first_name} ${profile.surname}` : 'Loading…'}
              {profile?.account_id && (
                <span data-testid="profile-header-account-id" className="px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-200 text-[10px] md:text-xs font-mono font-bold border border-cyan-400/30 flex-shrink-0">
                  #{profile.account_id}
                </span>
              )}
              {profile?.legacy_id && (
                <span
                  data-testid="profile-header-legacy-id"
                  className="px-2 py-0.5 rounded bg-violet-500/20 text-violet-200 text-[10px] md:text-xs font-mono font-bold border border-violet-400/30 flex-shrink-0"
                  title={`Legacy imported · legacy_id ${profile.legacy_id}`}>
                  legacy #{profile.legacy_id}
                </span>
              )}
              {profile && (
                <CustomerBadges
                  isCompany={profile.is_company}
                  companyName={profile.company_name}
                  registrationDate={profile.registration_date || profile.created_at}
                  newDays={newCustomerDays}
                  size="md"
                  testid="profile-header-badges"
                />
              )}
            </h2>
            <p className="text-[10px] md:text-xs text-slate-300 truncate">
              {profile?.email}{profile?.id ? ` · ${profile.id.substring(0, 8)}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
            {!editing && profile && profile.role !== 'admin' && (
              <button
                onClick={() => setEditing(true)}
                data-testid="profile-edit"
                className="px-2 md:px-3 py-1.5 bg-cyan-500 hover:bg-cyan-600 rounded-lg text-xs md:text-sm font-medium flex items-center gap-1.5"
              >
                <Edit2 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Edit</span>
              </button>
            )}
            {editing && (
              <>
                <button
                  onClick={() => { setDraft(profile); setEditing(false); setErr(''); }}
                  disabled={saving}
                  data-testid="profile-cancel"
                  className="px-2 md:px-3 py-1.5 bg-slate-600 hover:bg-slate-700 rounded-lg text-xs md:text-sm flex items-center gap-1.5"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Cancel</span>
                </button>
                <button
                  onClick={save}
                  disabled={saving || !dirty}
                  data-testid="profile-save"
                  className="px-2 md:px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 rounded-lg text-xs md:text-sm font-semibold flex items-center gap-1.5"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} <span className="hidden sm:inline">Save</span>
                </button>
              </>
            )}
            <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg" data-testid="profile-close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* View tabs */}
        {profile && (
          <div className="bg-white border-b border-slate-200 px-2 md:px-4 flex gap-1 overflow-x-auto no-scrollbar flex-shrink-0">
            {[
              { k: 'profile',      label: 'Profile',         icon: UserIcon },
              { k: 'login',        label: 'Login history',   icon: History },
              { k: 'transactions', label: 'Transactions',    icon: ListChecks },
              { k: 'stats',        label: 'Account statistic', icon: TrendingUp },
              { k: 'rating',       label: 'Client rating place', icon: Award },
            ].map(({ k, label, icon: Icon }) => (
              <button
                key={k}
                onClick={() => setView(k)}
                data-testid={`profile-tab-${k}`}
                className={`px-4 py-2.5 text-sm font-semibold flex items-center gap-1.5 border-b-2 transition ${
                  view === k
                    ? 'border-cyan-500 text-cyan-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Icon className="w-4 h-4" />{label}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : err && !profile ? (
          <div className="p-8 text-red-600">{err}</div>
        ) : !profile ? null : (
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {view === 'profile' && (
            <>
            {err && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="profile-err">
                {err}
              </div>
            )}

            {/* Account stats strip — 5 cards including the live EVC
                credit balance on the right so admins eyeball it without
                scrolling to the EVC section below. The EVC card hides
                its refresh affordance here (the dedicated Sync button
                lives in the EVC section). */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Stat label="Balance"   value={`€${(profile.balance || 0).toFixed(2)}`} icon={Wallet} color="emerald" />
              <Stat label="Reserved"  value={`€${(profile.reserved || 0).toFixed(2)}`} icon={Wallet} color="amber" />
              <Stat label="Recharged" value={`€${(profile.recharged_amount || 0).toFixed(2)}`} icon={Wallet} color="cyan" />
              <Stat label="Orders"    value={profile.orders_count || 0} icon={Calendar} color="slate" />
              <Stat
                label="EVC credits"
                value={
                  profile.evc_customer_id == null
                    ? '—'
                    : (profile.evc_balance == null ? '?' : `${profile.evc_balance}`)
                }
                icon={Coins}
                color={
                  profile.evc_customer_id == null
                    ? 'slate'
                    : (Number(profile.evc_balance) > 0 ? 'emerald' : 'slate')
                }
                testid="profile-stat-evc-balance"
              />
            </div>

            {/* Role banner */}
            <div className={`p-4 rounded-xl border ${TONE[roleMeta.color]} flex items-start gap-3`} data-testid={`role-banner-${role}`}>
              <RoleIcon className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm">Current role: {roleMeta.label}</p>
                <p className="text-xs opacity-80 mt-0.5">{roleMeta.hint}</p>
                {editing && profile.role !== 'admin' && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {['user', 'not_confirmed', 'access_denied'].map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => set('role', r)}
                        data-testid={`role-set-${r}`}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition ${
                          (draft?.role || 'user') === r
                            ? 'bg-white border-slate-400 text-slate-800 shadow'
                            : 'bg-white/50 border-transparent text-slate-600 hover:bg-white'
                        }`}
                      >
                        {ROLE_LABELS[r].label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Personal */}
            <Section title="Personal information" icon={UserIcon}>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-x-5 gap-y-3">
                <Field label="Account ID" value={profile.account_id ? `#${profile.account_id}` : ''} editing={false} readOnly testid="profile-account_id" />
                <Field label="Registered" value={profile.registration_date ? new Date(profile.registration_date).toLocaleString() : ''} editing={false} readOnly />
                <Field label="First name" value={draft?.first_name} onChange={(v) => set('first_name', v)} editing={editing} testid="profile-first_name" />
                <Field label="Surname"    value={draft?.surname}    onChange={(v) => set('surname', v)}    editing={editing} testid="profile-surname" />
                <Field label="Email"      value={draft?.email}      onChange={(v) => set('email', v)}      editing={editing} testid="profile-email" type="email" />
                <Field label="Country"    value={draft?.country}    onChange={(v) => set('country', v)}    editing={editing} testid="profile-country"
                       placeholder="— Select country —"
                       options={ALL_COUNTRIES.map((c) => ({ value: c.name, label: `${c.flag} ${c.name}` }))} />
                <Field label="Phone"      value={draft?.phone}      onChange={(v) => set('phone', v)}      editing={editing} testid="profile-phone" placeholder="+49 1512 345 6789" />
                <Field label="Phone country (ISO-2)" value={draft?.phone_country} onChange={(v) => set('phone_country', v)} editing={editing} testid="profile-phone_country" placeholder="DE" />
                {/* Quick-launch messengers — uses the SAVED phone
                    (not `draft`) so the buttons stay clickable while
                    the admin is mid-edit. Hidden when no phone. */}
                {profile.phone && (
                  <div className="md:col-span-2 xl:col-span-3 2xl:col-span-4 -mt-2" data-testid="profile-phone-messengers">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mr-2">Open in:</span>
                    <MessengerLinks phone={profile.phone} />
                  </div>
                )}
                <Field label="Group"      value={draft?.group}      onChange={(v) => set('group', v)}      editing={editing} testid="profile-group" placeholder="VIP, B2B, …" />
              </div>
            </Section>

            {/* EVC */}
            <Section title="EVC.de registration" icon={FileText}>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-5 gap-y-3">
                <Toggle
                  label="Registered at EVC.de"
                  value={editing ? !!draft?.evc_registered : !!profile.evc_registered}
                  onChange={editing ? ((v) => set('evc_registered', v)) : (() => {})}
                  hint="Customer has a WinOLS / EVC account."
                  testid="profile-evc_registered"
                />
                <Field
                  label="WinOLS / EVC customer ID"
                  value={draft?.evc_customer_id}
                  onChange={(v) => set('evc_customer_id', v)}
                  editing={editing && draft?.evc_registered}
                  testid="profile-evc_customer_id"
                />
                <Field
                  label="Reseller name (EVC.de)"
                  value={draft?.evc_reseller_name}
                  onChange={(v) => set('evc_reseller_name', v)}
                  editing={editing && draft?.evc_registered}
                  testid="profile-evc_reseller_name"
                  placeholder="e.g. JB Auto"
                />
              </div>

              {/* Live EVC balance block — read-only, with a "Sync now" button
                  that re-polls EVC and persists users.evc_balance so all
                  surfaces (this drawer, Customers list, the user's own EVC
                  dashboard) read the same number. Stays even outside edit
                  mode so admins can sync without entering edit mode. */}
              {profile?.evc_customer_id && (
                <div
                  className="mt-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200 flex flex-wrap items-center gap-3"
                  data-testid="profile-evc-balance-block"
                >
                  <div className="flex items-center gap-2 text-emerald-800">
                    <Coins className="w-5 h-5" />
                    <div>
                      <div className="text-[10px] uppercase font-bold tracking-wider text-emerald-700/70">Live EVC balance</div>
                      <div className="text-2xl font-bold tabular-nums" data-testid="profile-evc-balance-value">
                        {profile.evc_balance == null ? '—' : `${profile.evc_balance}`}
                        <span className="text-sm font-medium text-emerald-700/80 ml-1">credits</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-[11px] text-emerald-700/80">
                    {profile.evc_balance_synced_at
                      ? <>Last synced: <b>{new Date(profile.evc_balance_synced_at).toLocaleString()}</b></>
                      : <>Never synced — click <b>Sync now</b> to fetch from EVC.</>
                    }
                  </div>
                  <button
                    type="button"
                    onClick={syncEvcBalance}
                    disabled={evcSyncing}
                    className="ml-auto px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="profile-evc-sync-button"
                  >
                    <RefreshCw className={`w-4 h-4 ${evcSyncing ? 'animate-spin' : ''}`} />
                    {evcSyncing ? 'Syncing…' : 'Sync now'}
                  </button>
                  {evcSyncMsg && (
                    <div className="w-full text-[12px] text-emerald-800/90" data-testid="profile-evc-sync-msg">{evcSyncMsg}</div>
                  )}
                </div>
              )}
            </Section>

            {/* Company */}
            <Section title="Company / invoicing details" icon={Building2}>
              <Toggle
                label="Company account"
                value={editing ? !!draft?.is_company : !!profile.is_company}
                onChange={editing ? ((v) => set('is_company', v)) : (() => {})}
                hint="Toggling this enables/disables the company invoice block."
                testid="profile-is_company"
              />
              {(editing ? draft?.is_company : profile.is_company) && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-x-5 gap-y-3 mt-3">
                  <Field label="Company name" value={draft?.company_name} onChange={(v) => set('company_name', v)} editing={editing} testid="profile-company_name" />
                  <Field label="VAT"          value={draft?.vat}          onChange={(v) => set('vat', v)}          editing={editing} testid="profile-vat" />
                  <Field label="Address / PO Box" value={draft?.address}  onChange={(v) => set('address', v)}      editing={editing} testid="profile-address" />
                  <Field label="Zipcode"      value={draft?.zipcode}      onChange={(v) => set('zipcode', v)}      editing={editing} testid="profile-zipcode" />
                  <Field label="City"         value={draft?.city}         onChange={(v) => set('city', v)}         editing={editing} testid="profile-city" />
                </div>
              )}
            </Section>

            {/* Extended contacts & payment details */}
            <Section title="Contacts & payment details" icon={CreditCard} accent="violet">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-5 gap-y-3">
                <Field label="PayPal account address" value={draft?.paypal_account_address} onChange={(v) => set('paypal_account_address', v)} editing={editing} testid="profile-paypal_account_address" placeholder="paypal@example.com" />
                <Field label="Payment data (PayPal for payment)" value={draft?.payment_data} onChange={(v) => set('payment_data', v)} editing={editing} testid="profile-payment_data" placeholder="pay@example.com" />
                <Field label="Skype" value={draft?.skype} onChange={(v) => set('skype', v)} editing={editing} testid="profile-skype" placeholder="live:.cid.xxx" />
                <Field label="Messenger contacts (WhatsApp, Telegram, WeChat, etc.)" value={draft?.messenger_contacts} onChange={(v) => set('messenger_contacts', v)} editing={editing} testid="profile-messenger_contacts" placeholder="+49 1512 345 6789" />
                <Field label="From IP (registration)" value={profile.from_ip} editing={false} readOnly />
                <Field label="Last IP (last login)" value={profile.last_ip} editing={false} readOnly />
              </div>
            </Section>

            {/* Per-customer pricing (coefficient + group discount) */}
            <Section title="Pricing & discounts" icon={Wallet} accent="cyan">
              <p className="text-xs text-slate-500 mb-3 -mt-1">
                <strong>Coefficient</strong> multiplies every option price the customer sees (1.0 = standard,
                1.10 = +10%). Prices are rounded to the nearest €5. <strong>Group</strong> applies a discount on the final order total.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Price coefficient</label>
                  {editing ? (
                    <input
                      type="number" step="0.01" min="0.1" max="10"
                      value={draft?.price_coefficient ?? 1.0}
                      onChange={(e) => set('price_coefficient', e.target.value === '' ? 1.0 : parseFloat(e.target.value))}
                      data-testid="profile-price_coefficient"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 font-mono"
                    />
                  ) : (
                    <p data-testid="profile-price_coefficient" className="text-sm py-2 border-b border-slate-100 min-h-[36px] font-mono font-bold text-slate-800">
                      {Number(profile.price_coefficient ?? 1.0).toFixed(2)}
                      {Number(profile.price_coefficient ?? 1.0) !== 1.0 && (
                        <span className="ml-2 text-xs text-cyan-600 font-normal">
                          (+{Math.round((Number(profile.price_coefficient) - 1) * 100)}%)
                        </span>
                      )}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Customer group (discount tier)</label>
                  {editing ? (
                    <select
                      value={draft?.customer_group_id || ''}
                      onChange={(e) => set('customer_group_id', e.target.value || null)}
                      data-testid="profile-customer_group_id"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    >
                      <option value="">— No group (no discount) —</option>
                      {customerGroups.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}{g.discount_type === 'percent' ? ` · ${g.percent_value}% off` : ` · SEPR (${g.sepr_rules?.length || 0} ranges)`}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p data-testid="profile-customer_group_id" className="text-sm py-2 border-b border-slate-100 min-h-[36px] text-slate-800">
                      {(() => {
                        const gid = profile.customer_group_id;
                        const g = customerGroups.find((x) => x.id === gid);
                        if (!g) return <span className="text-slate-300 italic">— No group —</span>;
                        return (
                          <>
                            <strong>{g.name}</strong>
                            <span className="ml-2 text-xs text-slate-500">
                              {g.discount_type === 'percent' ? `· ${g.percent_value}% off` : `· SEPR staircase`}
                            </span>
                          </>
                        );
                      })()}
                    </p>
                  )}
                </div>
              </div>

              {/* PayPal routing block */}
              <div className="mt-5 pt-4 border-t border-slate-200" data-testid="profile-routing-block">
                <h4 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-indigo-600" /> PayPal routing
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Forced company (admin override)</label>
                    {editing ? (
                      <select
                        value={draft?.forced_company || ''}
                        onChange={(e) => set('forced_company', e.target.value || null)}
                        data-testid="profile-forced_company"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">— Auto (by country) —</option>
                        <option value="USA_COMPANY">Force → USA Company</option>
                        <option value="UK_COMPANY">Force → UK Company</option>
                        <option value="EU_COMPANY">Force → EU Company</option>
                      </select>
                    ) : (
                      <p data-testid="profile-forced_company" className="text-sm py-2 border-b border-slate-100 min-h-[36px] text-slate-800">
                        {profile.forced_company
                          ? <><span className="px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-100 text-indigo-700">FORCED</span> <strong className="ml-1.5">{profile.forced_company.replace('_COMPANY', '')}</strong></>
                          : <span className="text-slate-300 italic">— Auto-routed by country —</span>}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Current routing</label>
                    <div className="py-2 min-h-[36px]">
                      {routingInfo?.error === 'no_active_companies' ? (
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700" data-testid="profile-routing-error">NO ACTIVE COMPANIES</span>
                      ) : routingInfo?.role ? (
                        <div className="flex items-center flex-wrap gap-1.5 text-sm">
                          <span className="font-bold text-slate-800" data-testid="profile-routing-company">{routingInfo.company?.name}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                            routingInfo.routing_reason === 'FORCED' ? 'bg-indigo-100 text-indigo-700' :
                            routingInfo.routing_reason === 'FAILSAFE' ? 'bg-amber-100 text-amber-700' :
                            'bg-emerald-100 text-emerald-700'
                          }`} data-testid="profile-routing-reason">
                            {routingInfo.routing_reason}
                          </span>
                          {routingInfo.apply_tax && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700">+{routingInfo.tax_rate}% TAX</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-300 italic text-sm">Resolving…</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Section>

            {/* Permissions */}
            <Section title="Permissions & limits" icon={Shield} accent="amber">
              <div className="space-y-3">
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <Hash className="w-3.5 h-3.5" /> Credit limit (€)
                  </label>
                  {editing ? (
                    <div>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-slate-400 text-sm">€</span>
                        <input
                          type="number"
                          min="0" step="0.01"
                          value={draft?.credit_limit ?? 0}
                          onChange={(e) => set('credit_limit', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                          data-testid="profile-credit_limit"
                          className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        How far the customer is allowed to go into negative balance. <strong>0</strong> means strict (no overdraft). e.g. setting <strong>€100</strong> lets them place orders up to a balance of <strong>-€100</strong>.
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-800 font-semibold">
                      €{Number(profile.credit_limit || 0).toFixed(2)}
                      {!profile.credit_limit && <span className="ml-2 text-xs text-slate-400 font-normal">(strict — no overdraft)</span>}
                    </p>
                  )}
                </div>

                <Toggle
                  label="Disable recharge account"
                  value={editing ? !!draft?.recharge_disabled : !!profile.recharge_disabled}
                  onChange={editing ? ((v) => set('recharge_disabled', v)) : (() => {})}
                  hint="When enabled, the customer cannot top up their balance and sees an explanatory message on the Recharge page."
                  testid="profile-recharge_disabled"
                  color="red"
                />

                <Toggle
                  label="Account is active"
                  value={editing ? !!draft?.is_active : !!profile.is_active}
                  onChange={editing ? ((v) => set('is_active', v)) : (() => {})}
                  hint="If turned off, the user cannot log in (similar to deactivation)."
                  testid="profile-is_active"
                />

                {/* Trusted client — saves instantly (no edit mode needed),
                    because it is the lever an admin pulls while a regular
                    customer is waiting on the phone. */}
                <div className="p-3 rounded-lg border border-emerald-200 bg-emerald-50/60" data-testid="profile-trusted-block">
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!!profile.trusted}
                      onClick={toggleTrusted}
                      disabled={trustedBusy}
                      data-testid="profile-trusted-toggle"
                      className={`mt-0.5 relative w-11 h-6 shrink-0 rounded-full transition-colors disabled:opacity-50 ${profile.trusted ? 'bg-emerald-500' : 'bg-slate-300'}`}
                    >
                      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${profile.trusted ? 'left-[22px]' : 'left-0.5'}`} />
                    </button>
                    <div>
                      <p className="text-sm font-bold text-slate-800">
                        Trusted client
                        {trustedBusy && <Loader2 className="inline w-3.5 h-3.5 ml-2 animate-spin text-slate-400" />}
                      </p>
                      <p className="text-xs text-slate-600 mt-0.5">
                        Relaxes the anti-abuse limits (orders per 10 min / per day, original files per day).
                        Turn it on for a regular customer who hit a limit; turn it off to put them back under
                        the strict new-client caps. Caps are set in <strong>Settings → Limits &amp; anti-abuse</strong>.
                      </p>
                      {profile.trusted_set_by && (
                        <p className="text-[11px] text-slate-400 mt-1" data-testid="profile-trusted-meta">
                          Last changed by {profile.trusted_set_by}
                          {profile.trusted_set_at ? ` on ${new Date(profile.trusted_set_at).toLocaleString()}` : ''}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <Hash className="w-3.5 h-3.5" /> Individual recharge / balance cap (€)
                  </label>
                  {editing ? (
                    <div>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-slate-400 text-sm">€</span>
                        <input
                          type="number" min="0" step="1"
                          value={draft?.risk_balance_cap_eur ?? ''}
                          onChange={(e) => set('risk_balance_cap_eur', e.target.value === '' ? null : parseFloat(e.target.value))}
                          placeholder="Leave empty — use the global limit"
                          data-testid="profile-risk_balance_cap_eur"
                          className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Overrides the global <strong>max single top-up</strong> and <strong>max balance</strong> for this
                        customer only. A payment above it is held for review instead of being credited.
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-800 font-semibold" data-testid="profile-risk_balance_cap_eur">
                      {profile.risk_balance_cap_eur == null || profile.risk_balance_cap_eur === ''
                        ? <span className="text-slate-400 font-normal">— global limit —</span>
                        : `€${Number(profile.risk_balance_cap_eur).toFixed(2)}`}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Internal admin notes</label>
                  {editing ? (
                    <textarea
                      rows={3}
                      value={draft?.admin_notes || ''}
                      onChange={(e) => set('admin_notes', e.target.value)}
                      data-testid="profile-admin_notes"
                      placeholder="Notes only visible to admins (reason for blocking, special arrangements, …)"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  ) : (
                    <p className="text-sm text-slate-700 whitespace-pre-wrap min-h-[40px] bg-amber-50 border border-amber-100 rounded-lg p-2.5">
                      {profile.admin_notes || <span className="text-slate-300 italic">No notes</span>}
                    </p>
                  )}
                </div>
              </div>
            </Section>

            {/* Disabled priorities (admin gate per-customer) */}
            <Section title="Disabled order priorities" icon={Shield} accent="rose">
              <p className="text-xs text-slate-500 mb-3 -mt-1">
                Block specific priorities for this customer. They will not appear in the order wizard, and orders submitted via API with a disabled priority are rejected with HTTP 403.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                {['highest','high','medium','low','lowest'].map((p) => {
                  const disabledList = (editing ? draft : profile)?.disabled_priorities || [];
                  const checked = disabledList.includes(p);
                  return (
                    <label
                      key={p}
                      data-testid={`profile-disabled-priority-${p}`}
                      className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-xs font-bold uppercase tracking-wide cursor-pointer transition-all ${
                        checked
                          ? 'bg-rose-50 border-rose-300 text-rose-700'
                          : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                      } ${editing ? '' : 'opacity-80 cursor-not-allowed'}`}
                    >
                      <input
                        type="checkbox"
                        disabled={!editing}
                        checked={checked}
                        onChange={(e) => {
                          const cur = new Set(draft?.disabled_priorities || []);
                          if (e.target.checked) cur.add(p); else cur.delete(p);
                          set('disabled_priorities', [...cur]);
                        }}
                        className="accent-rose-500"
                      />
                      {p}
                    </label>
                  );
                })}
              </div>
              {((editing ? draft : profile)?.disabled_priorities || []).length === 0 && !editing && (
                <p className="text-xs text-slate-400 italic mt-2">All priorities are available for this customer.</p>
              )}
            </Section>

            {/* Manual balance adjustment (admin only) */}
            <Section title="Balance adjustment" icon={Wallet} accent="emerald">
              <p className="text-xs text-slate-500 mb-4 -mt-1">
                Manually credit or deduct from this customer's balance. A transaction is logged and can be deleted (rolling back the balance) from the Transactions page. Optionally generate a PDF invoice for credits and email it to the customer.
              </p>

              {/* Kind toggle */}
              <div className="grid grid-cols-2 gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setAdjKind('credit')}
                  data-testid="adj-kind-credit"
                  className={`px-3 py-2.5 rounded-lg border text-sm font-semibold flex items-center justify-center gap-2 transition ${
                    adjKind === 'credit'
                      ? 'bg-emerald-500 text-white border-emerald-500 shadow'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-emerald-50 hover:border-emerald-200'
                  }`}
                >
                  <PlusCircle className="w-4 h-4" /> Add credit
                </button>
                <button
                  type="button"
                  onClick={() => setAdjKind('debit')}
                  data-testid="adj-kind-debit"
                  className={`px-3 py-2.5 rounded-lg border text-sm font-semibold flex items-center justify-center gap-2 transition ${
                    adjKind === 'debit'
                      ? 'bg-rose-500 text-white border-rose-500 shadow'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-rose-50 hover:border-rose-200'
                  }`}
                >
                  <MinusCircle className="w-4 h-4" /> Deduct credit
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Amount (€) *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-slate-400 text-sm">€</span>
                    <input
                      type="number"
                      min="0.01" step="0.01"
                      value={adjAmount}
                      onChange={(e) => setAdjAmount(e.target.value)}
                      placeholder="100.00"
                      data-testid="adj-amount"
                      className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Description *</label>
                  <input
                    type="text"
                    value={adjDescription}
                    onChange={(e) => setAdjDescription(e.target.value)}
                    placeholder={adjKind === 'credit'
                      ? 'e.g. Goodwill credit / Manual top-up after PayPal dispute'
                      : 'e.g. Refund correction / Admin adjustment'}
                    data-testid="adj-description"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              {adjKind === 'credit' && (
                <label className="flex items-start gap-3 p-3 mt-3 rounded-lg border border-emerald-200 bg-emerald-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={adjGenerateInvoice}
                    onChange={(e) => setAdjGenerateInvoice(e.target.checked)}
                    data-testid="adj-generate-invoice"
                    className="mt-0.5 w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-emerald-900 flex items-center gap-1.5">
                      <Receipt className="w-4 h-4" /> Generate PDF invoice & email to customer
                    </p>
                    <p className="text-xs text-emerald-800 mt-0.5">
                      Uses the default seller company from <strong>Settings → My Companies</strong>. The customer will be able to re-download the invoice from their Transactions page.
                    </p>
                  </div>
                </label>
              )}

              {adjErr && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="adj-error">
                  {adjErr}
                </div>
              )}
              {adjResult && (
                <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800" data-testid="adj-success">
                  {adjResult.kind === 'credit' ? <PlusCircle className="w-4 h-4 inline mr-1.5" /> : <MinusCircle className="w-4 h-4 inline mr-1.5" />}
                  Balance updated: <strong>€{Number(adjResult.balance_before || 0).toFixed(2)}</strong> → <strong>€{Number(adjResult.balance_after || 0).toFixed(2)}</strong>
                  {adjResult.invoice_generated && <span className="block mt-1 text-emerald-700"><Receipt className="w-3.5 h-3.5 inline mr-1" /> Invoice generated and emailed.</span>}
                </div>
              )}

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={submitAdjustment}
                  disabled={adjBusy || !adjAmount || !adjDescription.trim()}
                  data-testid="adj-submit"
                  className={`px-5 py-2 text-white rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50 ${
                    adjKind === 'credit' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-rose-500 hover:bg-rose-600'
                  }`}
                >
                  {adjBusy
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : (adjKind === 'credit'
                        ? <ArrowDownLeft className="w-4 h-4" />
                        : <ArrowUpRight className="w-4 h-4" />)}
                  {adjKind === 'credit' ? 'Add credit' : 'Deduct credit'}
                </button>
              </div>
            </Section>

            {/* Postpaid / "Order without prepayment" — admin-controlled
                permission allowing the customer to submit orders that
                push the wallet into the negative, up to the configured
                limit. Disabled by default; the legacy `credit_limit`
                field in Permissions remains for backward compatibility. */}
            <Section title="Order without prepayment" icon={Shield} accent="amber">
              <p className="text-xs text-slate-500 mb-4 -mt-1">
                Trusted customers can be allowed to submit orders even when their balance is insufficient.
                When enabled, orders may drive the wallet into the negative, up to the configured limit.
                <span className="block mt-1 text-amber-700">
                  Recharge bonuses are not granted while the customer's balance is negative — this rule is enforced automatically.
                </span>
              </p>

              {/* Current status badge */}
              <div className="mb-4 flex flex-wrap items-center gap-2">
                {profile.allow_postpaid_orders ? (
                  <>
                    <span
                      className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200"
                      data-testid="postpaid-status-pill"
                    >
                      Postpaid enabled
                    </span>
                    {profile.postpaid_credit_limit > 0 && (
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-700 border border-slate-200" data-testid="postpaid-limit-pill">
                        Limit: €{Number(profile.postpaid_credit_limit).toFixed(2)}
                      </span>
                    )}
                    {profile.postpaid_valid_until && (
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-700 border border-slate-200" data-testid="postpaid-expiry-pill">
                        Valid until {String(profile.postpaid_valid_until).slice(0, 10)}
                      </span>
                    )}
                    {profile.postpaid?.expired && (
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-rose-100 text-rose-700 border border-rose-200" data-testid="postpaid-expired-pill">
                        Expired — orders will be rejected
                      </span>
                    )}
                  </>
                ) : (
                  <span
                    className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-500 border border-slate-200"
                    data-testid="postpaid-status-pill"
                  >
                    Postpaid disabled
                  </span>
                )}
                {profile.postpaid_updated_at && (
                  <span className="text-[11px] text-slate-500" data-testid="postpaid-updated-meta">
                    Updated {String(profile.postpaid_updated_at).slice(0, 10)}
                    {profile.postpaid_updated_by ? ` by ${profile.postpaid_updated_by}` : ''}
                  </span>
                )}
              </div>

              <div className="space-y-3">
                <Toggle
                  label="Allow order without prepayment"
                  value={ppEnabled}
                  onChange={(v) => setPpEnabled(v)}
                  hint="When on, the customer can submit orders that drive their balance below €0 — up to the configured limit."
                  testid="postpaid-toggle"
                  color="emerald"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">
                      Negative balance limit (€) *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-slate-400 text-sm">€</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={ppLimit}
                        onChange={(e) => setPpLimit(e.target.value)}
                        placeholder="e.g. 500"
                        disabled={!ppEnabled}
                        data-testid="postpaid-limit-input"
                        className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:bg-slate-50 disabled:text-slate-400"
                      />
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1">
                      Customer may place orders until balance reaches <strong>−€{ppLimit && Number(ppLimit) > 0 ? Number(ppLimit).toFixed(2) : '—'}</strong>.
                    </p>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">
                      Valid until (optional)
                    </label>
                    <input
                      type="date"
                      value={ppValidUntil}
                      onChange={(e) => setPpValidUntil(e.target.value)}
                      disabled={!ppEnabled}
                      data-testid="postpaid-valid-until-input"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:bg-slate-50 disabled:text-slate-400"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">
                      Leave empty for no expiry. After this date, postpaid permission is automatically inactive.
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">
                    Internal reason / note (optional)
                  </label>
                  <textarea
                    rows={2}
                    value={ppNote}
                    onChange={(e) => setPpNote(e.target.value)}
                    placeholder="e.g. Trusted customer, pays weekly by bank transfer."
                    data-testid="postpaid-note-input"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Visible to admins only — never shown to the customer.
                  </p>
                </div>

                {ppErr && (
                  <div className="bg-rose-50 border border-rose-200 rounded-lg p-2.5 text-sm text-rose-700" data-testid="postpaid-error">
                    {ppErr}
                  </div>
                )}
                {ppMsg && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 text-sm text-emerald-700" data-testid="postpaid-success">
                    {ppMsg}
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={submitPostpaid}
                    disabled={ppBusy}
                    data-testid="postpaid-save-btn"
                    className="px-5 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                  >
                    {ppBusy
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Save className="w-4 h-4" />}
                    Save postpaid permission
                  </button>
                </div>
              </div>
            </Section>

            {/* Legal acceptance (read-only) */}
            <Section title="Legal acceptance (read-only)" icon={FileText}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <LegalRow label="License agreement" value={profile.accepted_license} />
                <LegalRow label="Terms of service"  value={profile.accepted_terms} />
                <LegalRow label="Refund policy"     value={profile.accepted_refund} />
                <LegalRow label="Newsletter"        value={profile.subscribed_newsletter} optional />
              </div>
            </Section>

            </>
            )}

            {view === 'stats' && stats && (
              <AccountStatisticView stats={stats.stats} />
            )}

            {view === 'rating' && (
              <ClientRatingPlaceView stats={stats?.rating} rank={rank} onReload={loadRank} />
            )}

            {view === 'login' && (
              <LoginHistoryView
                stats={loginStats}
                items={loginItems}
                loading={loginLoading}
                filter={loginFilter}
                setFilter={setLoginFilter}
                onFilter={loadLoginHistory}
                onToggleBlock={toggleBlockLogin}
              />
            )}

            {view === 'transactions' && (
              <TransactionsListView
                token={token}
                customerId={customerId}
                items={txnItems}
                loading={txnLoading}
                onReload={loadTransactions}
                onProfileMutated={reloadProfile}
                onOpenOrder={(orderId) => {
                  if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('admin:open-order', { detail: { orderId } }));
                  }
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  ), document.body);
}

const StatRow = ({ label, value, color = 'slate' }) => (
  <div className={`bg-${color}-50/60 border border-${color}-100 rounded-lg px-3 py-2`}>
    <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wide">{label}</p>
    <p className="text-sm font-bold text-slate-800 mt-0.5">{value}</p>
  </div>
);

const AccountStatisticView = ({ stats }) => {
  // Lightweight inline chart — visualises the three income windows (30d
  // / year / all-time). Pure SVG, no chart library so we don't pull
  // recharts/d3 into the bundle just for this one card. Bar height is
  // normalised against the max so even a customer with only a single
  // 30d income still gets a useful chart.
  const incomes = [
    { lbl: '30d',  val: Number(stats.income_30d || 0)  },
    { lbl: 'Year', val: Number(stats.income_year || 0) },
    { lbl: 'All',  val: Number(stats.income_all || 0)  },
  ];
  const max = Math.max(1, ...incomes.map((x) => x.val));
  return (
  <div className="space-y-4" data-testid="account-statistic-view">
    {/* Top KPI strip — bigger, more breathable than the dense flat grid below. */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-xl p-4">
        <p className="text-[10px] uppercase font-bold text-emerald-700 tracking-wider">Successful · 30d</p>
        <p className="text-3xl font-black text-emerald-700 mt-1">{stats.successful_count_30d}</p>
        <p className="text-xs text-emerald-700/70 mt-1">€{stats.successful_amount_30d} total</p>
      </div>
      <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 border border-cyan-200 rounded-xl p-4">
        <p className="text-[10px] uppercase font-bold text-cyan-700 tracking-wider">All-time orders</p>
        <p className="text-3xl font-black text-cyan-700 mt-1">{stats.successful_count_all}</p>
        <p className="text-xs text-cyan-700/70 mt-1">€{stats.successful_amount_all} total</p>
      </div>
      <div className="bg-gradient-to-br from-violet-50 to-violet-100 border border-violet-200 rounded-xl p-4">
        <p className="text-[10px] uppercase font-bold text-violet-700 tracking-wider">Avg / month</p>
        <p className="text-3xl font-black text-violet-700 mt-1">{stats.avg_orders_per_month}</p>
        <p className="text-xs text-violet-700/70 mt-1">€{stats.avg_amount_per_month} / mo</p>
      </div>
      <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 rounded-xl p-4">
        <p className="text-[10px] uppercase font-bold text-amber-700 tracking-wider">Income · all time</p>
        <p className="text-3xl font-black text-amber-700 mt-1">€{stats.income_all}</p>
        <p className="text-xs text-amber-700/70 mt-1">over {stats.days_since_registration} days</p>
      </div>
    </div>

    {/* Income breakdown chart + secondary stats */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div className="md:col-span-2 bg-white rounded-xl border border-slate-200 p-4">
        <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-emerald-600" /> Income breakdown
        </h3>
        <div className="flex items-end justify-around h-44 gap-6 px-4">
          {incomes.map((b, i) => {
            const h = Math.max(4, Math.round((b.val / max) * 150));
            const colors = ['from-emerald-400 to-emerald-600', 'from-cyan-400 to-cyan-600', 'from-violet-400 to-violet-600'];
            return (
              <div key={b.lbl} className="flex flex-col items-center gap-2 flex-1">
                <span className="text-xs font-bold text-slate-700">€{b.val.toFixed(0)}</span>
                <div className={`w-full rounded-t-lg bg-gradient-to-t ${colors[i]} shadow-inner`} style={{ height: `${h}px`, transition: 'height 600ms ease' }} />
                <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">{b.lbl}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h3 className="text-sm font-bold text-slate-800 mb-3">Closed / cancelled</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Closed orders</span><span className="font-bold">{stats.closed_count_all}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Closed amount</span><span className="font-bold">€{stats.closed_amount_all}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Avg / day (lifetime)</span><span className="font-bold">{stats.avg_orders_per_day}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Avg amount / order</span><span className="font-bold">€{stats.avg_amount_per_order_all}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Days since signup</span><span className="font-bold">{stats.days_since_registration}d</span></div>
        </div>
      </div>
    </div>

    {/* Detailed flat table for the rest, in case admin wants raw values */}
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <h3 className="text-sm font-bold text-slate-800 mb-3">All statistics</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <StatRow label="Successful · 30d" value={stats.successful_count_30d} />
        <StatRow label="Successful · all" value={stats.successful_count_all} />
        <StatRow label="Avg / month" value={stats.avg_orders_per_month} />
        <StatRow label="Avg / day" value={`${stats.avg_orders_per_day} / day`} />
        <StatRow label="Avg amount / month" value={`€${stats.avg_amount_per_month}`} />
        <StatRow label="Avg amount / order" value={`€${stats.avg_amount_per_order_all}`} />
        <StatRow label="Closed · count" value={stats.closed_count_all} />
        <StatRow label="Closed · amount" value={`€${stats.closed_amount_all}`} />
        <StatRow label="Income · 30d"     value={`€${stats.income_30d}`}  color="cyan" />
        <StatRow label="Income · year"    value={`€${stats.income_year}`} color="cyan" />
        <StatRow label="Income · all"     value={`€${stats.income_all}`}  color="cyan" />
        <StatRow label="Successful · €30d" value={`€${stats.successful_amount_30d}`} />
      </div>
    </div>
  </div>
  );
};

const ClientRatingPlaceView = ({ stats, rank, onReload }) => {
  if (!stats) return <div className="text-center text-slate-500 py-10"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  // Each tier returns its FULL tailwind class literals (JIT won't scan dynamic strings).
  const tierBadge = (() => {
    const p = rank ? rank.percentile : 0;
    if (p >= 99) return { label: 'TOP 1% · LEGENDARY',   classes: 'bg-gradient-to-br from-amber-400 to-orange-500 text-amber-50' };
    if (p >= 95) return { label: 'TOP 5% · PLATINUM',    classes: 'bg-gradient-to-br from-indigo-500 to-purple-600 text-indigo-50' };
    if (p >= 90) return { label: 'TOP 10% · GOLD',       classes: 'bg-gradient-to-br from-yellow-500 to-amber-600 text-yellow-50' };
    if (p >= 75) return { label: 'TOP 25% · SILVER',     classes: 'bg-gradient-to-br from-slate-400 to-slate-600 text-slate-50' };
    if (p >= 50) return { label: 'TOP 50% · BRONZE',     classes: 'bg-gradient-to-br from-orange-400 to-red-500 text-orange-50' };
    return { label: 'NEW / INACTIVE', classes: 'bg-gradient-to-br from-slate-300 to-slate-500 text-slate-50' };
  })();

  return (
    <div className="space-y-4" data-testid="client-rating-place-view">
      {/* Big rank card */}
      {rank && (
        <div className={`rounded-2xl p-6 shadow-xl border border-white/20 ${tierBadge.classes}`} data-testid="rating-rank-card">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-widest font-bold opacity-80">Rank by value</p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-6xl font-black" data-testid="rating-rank-number">#{rank.rank}</span>
                <span className="text-xl font-bold opacity-80">/ {rank.total_customers.toLocaleString()}</span>
              </div>
              <p className="text-xs mt-1 opacity-80">{rank.active_customers.toLocaleString()} active customers (with at least one order or recharge)</p>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full border border-white/30">
                <Award className="w-4 h-4" />
                <span className="text-sm font-black tracking-wide" data-testid="rating-tier-badge">{tierBadge.label}</span>
              </div>
              <div className="mt-3">
                <p className="text-[11px] uppercase tracking-wide opacity-80">Percentile</p>
                <p className="text-3xl font-black" data-testid="rating-percentile">{rank.percentile}%</p>
              </div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
            <div className="bg-white/15 backdrop-blur-sm rounded-lg px-3 py-2">
              <p className="uppercase opacity-70 text-[10px] font-bold">Your score</p>
              <p className="text-base font-bold mt-0.5">€{rank.score.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
            </div>
            <div className="bg-white/15 backdrop-blur-sm rounded-lg px-3 py-2">
              <p className="uppercase opacity-70 text-[10px] font-bold">Avg (active)</p>
              <p className="text-base font-bold mt-0.5">€{rank.avg_score.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
            </div>
            <div className="bg-white/15 backdrop-blur-sm rounded-lg px-3 py-2">
              <p className="uppercase opacity-70 text-[10px] font-bold">Top score</p>
              <p className="text-base font-bold mt-0.5">€{rank.max_score.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
            </div>
          </div>
          <p className="text-[10px] mt-3 opacity-70 italic">Formula: {rank.formula}</p>
        </div>
      )}
      {!rank && (
        <div className="text-center text-slate-500 py-6 flex flex-col items-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-xs">Calculating rank against {'>'}5 000 customers…</span>
        </div>
      )}

      {/* Breakdown table */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
            <Award className="w-4 h-4 text-amber-600" /> Breakdown
          </h3>
          <button onClick={onReload} className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded-lg flex items-center gap-1">
            <RotateCcw className="w-3 h-3" /> Refresh rank
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-100">
                <th className="px-3 py-2 text-left text-xs font-bold text-slate-600 uppercase"></th>
                <th className="px-3 py-2 text-right text-xs font-bold text-slate-600 uppercase">This month</th>
                <th className="px-3 py-2 text-right text-xs font-bold text-slate-600 uppercase">This year</th>
                <th className="px-3 py-2 text-right text-xs font-bold text-slate-600 uppercase">All time</th>
              </tr>
            </thead>
            <tbody>
              <RatingRow label="Completed orders amount"  data={stats.completed_orders} unit="€" />
              <RatingRow label="Recharged amount"         data={stats.recharged}        unit="€" />
              <RatingRow label="Closed orders amount"     data={stats.closed_orders}    unit="€" />
              <RatingRow label="Closed orders quantity"   data={stats.closed_orders}    unit=""  asCount />
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const RatingRow = ({ label, data, unit, asCount = false }) => {
  const k = asCount ? { m: data.month_count, y: data.year_count, a: data.all_count } : { m: data.month_amount, y: data.year_amount, a: data.all_amount };
  const fmt = (v) => asCount ? v : `${unit}${Number(v).toFixed(2)}`;
  return (
    <tr className="border-b border-slate-100">
      <td className="px-3 py-2 font-medium text-slate-700">{label}</td>
      <td className="px-3 py-2 text-right font-mono">{fmt(k.m)}</td>
      <td className="px-3 py-2 text-right font-mono">{fmt(k.y)}</td>
      <td className="px-3 py-2 text-right font-mono font-semibold">{fmt(k.a)}</td>
    </tr>
  );
};

const LoginHistoryView = ({ stats, items, loading, filter, setFilter, onFilter, onToggleBlock }) => (
  <div className="space-y-4" data-testid="login-history-view">
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2"><Filter className="w-4 h-4" /> Filter</h3>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <input type="text" placeholder="IP" value={filter.ip} onChange={(e) => setFilter({ ...filter, ip: e.target.value })}
          data-testid="login-filter-ip"
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500" />
        <input type="date" placeholder="Date start" value={filter.date_start} onChange={(e) => setFilter({ ...filter, date_start: e.target.value })}
          data-testid="login-filter-date-start"
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm" />
        <input type="date" placeholder="Date end" value={filter.date_end} onChange={(e) => setFilter({ ...filter, date_end: e.target.value })}
          data-testid="login-filter-date-end"
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm" />
        <button onClick={onFilter} data-testid="login-filter-apply"
          className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-2">
          <Filter className="w-4 h-4" /> Filter
        </button>
      </div>
    </div>

    {stats && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h4 className="text-xs font-bold text-slate-500 uppercase mb-2">Success orders</h4>
          <div className="grid grid-cols-5 gap-2 text-center">
            {['day','week','month','year','from_filter_range'].map((k) => (
              <div key={k} className="bg-emerald-50 rounded-lg p-2 border border-emerald-100">
                <p className="text-[10px] uppercase text-slate-500">{k.replace('_',' ')}</p>
                <p className="text-sm font-bold text-emerald-700">{stats.success_orders[k] ?? '—'}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h4 className="text-xs font-bold text-slate-500 uppercase mb-2">Entered</h4>
          <div className="grid grid-cols-5 gap-2 text-center">
            {['day','week','month','year','from_filter_range'].map((k) => (
              <div key={k} className="bg-cyan-50 rounded-lg p-2 border border-cyan-100">
                <p className="text-[10px] uppercase text-slate-500">{k.replace('_',' ')}</p>
                <p className="text-sm font-bold text-cyan-700">{stats.entered[k] ?? '—'}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    )}

    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-100 text-xs uppercase font-bold text-slate-600">
            <th className="px-3 py-2 text-left">Account ID</th>
            <th className="px-3 py-2 text-left">IP</th>
            <th className="px-3 py-2 text-left">Country</th>
            <th className="px-3 py-2 text-left">Device</th>
            <th className="px-3 py-2 text-left">Status</th>
            <th className="px-3 py-2 text-left">Created</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={7} className="text-center py-10 text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
          ) : items.length === 0 ? (
            <tr><td colSpan={7} className="text-center py-10 text-slate-500">No login events.</td></tr>
          ) : items.map((r) => (
            <tr key={r.id} className={`border-b border-slate-100 ${r.blocked ? 'bg-red-50' : ''}`} data-testid={`login-row-${r.id}`}>
              <td className="px-3 py-2 font-mono text-xs font-bold text-cyan-700">{r.account_id ? `#${r.account_id}` : '—'}</td>
              <td className="px-3 py-2 font-mono text-xs">
                {r.ip ? (
                  <a
                    href={`https://www.google.com/maps/search/${encodeURIComponent(`${r.city ? r.city + ', ' : ''}${r.country || ''}`.trim() || r.ip)}`}
                    target="_blank" rel="noreferrer"
                    title={`Open map for ${[r.city, r.country].filter(Boolean).join(', ') || r.ip}`}
                    className="text-cyan-700 hover:text-cyan-900 hover:underline cursor-pointer inline-flex items-center gap-1"
                    data-testid={`login-ip-link-${r.id}`}
                  >
                    {r.ip}
                  </a>
                ) : '—'}
              </td>
              <td className="px-3 py-2 text-xs">
                {r.country_code && (
                  <span className="mr-1" title={r.country}>{flagEmoji(r.country_code)}</span>
                )}
                <span className="text-slate-700">{r.country || '—'}</span>
                {r.city && <span className="text-slate-400">, {r.city}</span>}
              </td>
              <td className="px-3 py-2 text-xs">
                <span className="px-1.5 py-0.5 bg-slate-100 rounded text-[10px] font-medium text-slate-700 uppercase">{r.device_type || 'unknown'}</span>
                {(r.browser || r.os) && <span className="ml-1 text-slate-500 text-[11px]">{[r.browser, r.os].filter(Boolean).join(' · ')}</span>}
              </td>
              <td className="px-3 py-2">
                {r.success
                  ? <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[10px] font-bold">SUCCESS</span>
                  : <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold" title={r.reason}>FAILED</span>}
              </td>
              <td className="px-3 py-2 text-xs whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</td>
              <td className="px-3 py-2 text-right">
                <button onClick={() => onToggleBlock(r.id)} data-testid={`login-block-${r.id}`}
                  className={`px-3 py-1 rounded text-xs font-semibold ${r.blocked ? 'bg-slate-100 text-slate-700' : 'bg-red-500 text-white hover:bg-red-600'}`}>
                  {r.blocked ? 'Unblock' : 'Block'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  </div>
);

const flagEmoji = (code) => {
  if (!code || code.length !== 2) return '';
  const base = 0x1F1E6;
  return String.fromCodePoint(base + code.toUpperCase().charCodeAt(0) - 65, base + code.toUpperCase().charCodeAt(1) - 65);
};

const TransactionsListView = ({ token, customerId, items, loading, onReload, onProfileMutated, onOpenOrder }) => {
  // NOTE: transaction `status` filter intentionally removed. Every transaction
  // in this app ends up in `end` (finalized) or `tested` (reserved) quickly,
  // so filtering by it rarely yielded useful results and admins complained
  // about the column. The underlying column still exists; re-add the
  // dropdown here if the product requirement changes.
  const [filter, setFilter] = useState({
    description: '', type: '', direction: '',
    date_start: '', date_end: '',
  });
  const [filtered, setFiltered] = useState(items);
  const [exporting, setExporting] = useState(null); // 'excel' | 'pdf' | null
  const [deletingId, setDeletingId] = useState(null);

  React.useEffect(() => { setFiltered(items); }, [items]);

  const applyFilter = () => {
    const match = items.filter((t) => {
      if (filter.description && !(t.description || '').toLowerCase().includes(filter.description.toLowerCase())) return false;
      if (filter.type && t.type !== filter.type) return false;
      if (filter.direction && t.direction !== filter.direction) return false;
      if (filter.date_start && (t.created_at || '') < filter.date_start) return false;
      if (filter.date_end) {
        const end = filter.date_end.length === 10 ? `${filter.date_end}T23:59:59` : filter.date_end;
        if ((t.created_at || '') > end) return false;
      }
      return true;
    });
    setFiltered(match);
  };

  const resetFilter = () => {
    setFilter({ description: '', type: '', direction: '', date_start: '', date_end: '' });
    setFiltered(items);
  };

  const download = async (format) => {
    setExporting(format);
    try {
      const params = new URLSearchParams({ format, user_id: customerId });
      if (filter.date_start) params.append('date_start', filter.date_start);
      if (filter.date_end)   params.append('date_end',   filter.date_end);
      if (filter.type)       params.append('type',       filter.type);
      if (filter.direction)  params.append('direction',  filter.direction);
      if (filter.description) params.append('description', filter.description);
      const r = await fetch(`${API_URL}/api/transactions/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      a.href = url;
      a.download = `transactions-${customerId.slice(0, 8)}${filter.date_start || filter.date_end ? '-filtered' : '-all'}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(`Export failed: ${e.message}`);
    } finally { setExporting(null); }
  };

  const totals = filtered.reduce((acc, t) => {
    const amt = Number(t.amount || 0);
    if (t.direction === 'to_user') acc.credit += amt; else acc.debit += amt;
    return acc;
  }, { credit: 0, debit: 0 });

  // Deletion of a manual admin_adjustment or bonus companion row.
  // Two shapes are deletable (mirrors backend logic in
  // routes/transactions.py:admin_delete_transaction):
  //   1) `manual_kind` = 'credit' | 'debit' — Manual credit/debit rows.
  //   2) `is_bonus` = true — auto-generated bonus companion rows next
  //      to PayPal/Stripe recharges. Useful when an admin realises the
  //      recharge already carried the bonus and wants to revoke a
  //      duplicate they added by hand.
  const isDeletableAdjustment = (t) =>
    t?.type === 'admin_adjustment'
    && (t?.manual_kind === 'credit' || t?.manual_kind === 'debit' || !!t?.is_bonus);

  const deleteAdjustment = async (t) => {
    if (!isDeletableAdjustment(t)) return;
    const amount = `€${Number(t.amount || 0).toFixed(2)}`;
    const verb =
      t.manual_kind === 'debit'
        ? `Delete this manual debit of ${amount}`
        : t.is_bonus && t.manual_kind !== 'credit'
          ? `Delete this bonus row of ${amount}`
          : `Delete this manual credit of ${amount}`;
    if (!window.confirm(`${verb}?\n\nThe transaction will be removed AND the customer's balance rolled back by the opposite amount.`)) return;
    setDeletingId(t.id);
    try {
      const r = await fetch(`${API_URL}/api/transactions/admin/${t.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      onReload && onReload();
      // Also refresh the parent profile so the Balance / Reserved
      // header cards reflect the rollback that the DELETE endpoint
      // performed. Without this the ledger goes back in sync but the
      // Balance card keeps its pre-delete value until the drawer is
      // closed and reopened.
      onProfileMutated && onProfileMutated();
    } catch (e) {
      alert(`Delete failed: ${e.message || e}`);
    } finally {
      setDeletingId(null);
    }
  };

  return (
  <div className="space-y-3" data-testid="customer-txns-view">
    {/* Filters */}
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <input type="text" placeholder="Search description" value={filter.description}
          onChange={(e) => setFilter({ ...filter, description: e.target.value })}
          data-testid="customer-txns-filter-description"
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500" />
        <select value={filter.type} onChange={(e) => setFilter({ ...filter, type: e.target.value })}
          data-testid="customer-txns-filter-type"
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white">
          <option value="">All types</option>
          <option value="order">Order</option>
          <option value="recharge">Recharge</option>
          <option value="refund">Refund</option>
          <option value="admin_adjustment">Admin adjustment</option>
          <option value="bonus">Bonus</option>
        </select>
        <select value={filter.direction} onChange={(e) => setFilter({ ...filter, direction: e.target.value })}
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white">
          <option value="">All directions</option>
          <option value="to_user">Credit (to user)</option>
          <option value="from_user">Debit (from user)</option>
        </select>
        <input type="date" value={filter.date_start} onChange={(e) => setFilter({ ...filter, date_start: e.target.value })}
          data-testid="customer-txns-filter-date-start"
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm" />
        <input type="date" value={filter.date_end} onChange={(e) => setFilter({ ...filter, date_end: e.target.value })}
          data-testid="customer-txns-filter-date-end"
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm" />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button onClick={applyFilter} data-testid="customer-txns-filter-apply"
          className="px-4 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs rounded-lg font-semibold flex items-center gap-1.5">
          <Filter className="w-3.5 h-3.5" /> Apply
        </button>
        <button onClick={resetFilter}
          className="px-4 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs rounded-lg font-semibold flex items-center gap-1.5">
          <RotateCcw className="w-3.5 h-3.5" /> Reset
        </button>
        <div className="flex-1" />
        <button onClick={onReload}
          className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs rounded-lg font-semibold flex items-center gap-1.5">
          <RotateCcw className="w-3.5 h-3.5" /> Reload
        </button>
        <button onClick={() => download('excel')} disabled={!!exporting}
          data-testid="customer-txns-export-excel"
          className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded-lg font-semibold flex items-center gap-1.5 disabled:opacity-50">
          {exporting === 'excel' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowDownLeft className="w-3.5 h-3.5" />} Excel
        </button>
        <button onClick={() => download('pdf')} disabled={!!exporting}
          data-testid="customer-txns-export-pdf"
          className="px-4 py-1.5 bg-rose-500 hover:bg-rose-600 text-white text-xs rounded-lg font-semibold flex items-center gap-1.5 disabled:opacity-50">
          {exporting === 'pdf' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} PDF
        </button>
      </div>
    </div>

    {/* Summary */}
    <div className="grid grid-cols-3 gap-3">
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
        <p className="text-[10px] uppercase font-bold text-slate-500">Shown</p>
        <p className="text-lg font-bold text-slate-800">{filtered.length}</p>
      </div>
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
        <p className="text-[10px] uppercase font-bold text-emerald-700">Credit total</p>
        <p className="text-lg font-bold text-emerald-700">€{totals.credit.toFixed(2)}</p>
      </div>
      <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
        <p className="text-[10px] uppercase font-bold text-rose-700">Debit total</p>
        <p className="text-lg font-bold text-rose-700">€{totals.debit.toFixed(2)}</p>
      </div>
    </div>

    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-100 text-xs uppercase font-bold text-slate-600">
            <th className="px-3 py-2 text-left">Date</th>
            <th className="px-3 py-2 text-left">Type</th>
            <th className="px-3 py-2 text-left">Order</th>
            <th className="px-3 py-2 text-left">Direction</th>
            <th className="px-3 py-2 text-right">Amount</th>
            <th className="px-3 py-2 text-left">Status</th>
            <th className="px-3 py-2 text-left">External TXN</th>
            <th className="px-3 py-2 text-left">Description</th>
            <th className="px-3 py-2 text-right w-10"></th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={9} className="text-center py-10 text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
          ) : filtered.length === 0 ? (
            <tr><td colSpan={9} className="text-center py-10 text-slate-500">No transactions match the filter.</td></tr>
          ) : filtered.map((t) => {
              // External payment TXN extraction. Backend stores PayPal /
              // Stripe ids in `paypal_txn_id` / `stripe_payment_id` /
              // `provider_txn_id` depending on the integration. Pull
              // whatever exists and let admin click to copy.
              const extTxn = t.paypal_txn_id || t.stripe_payment_id || t.provider_txn_id || t.transaction_ref || null;
              const extKind = t.paypal_txn_id ? 'PayPal' : t.stripe_payment_id ? 'Stripe' : (extTxn ? 'EXT' : '');
              return (
            <tr key={t.id} className={`border-b border-slate-100 ${t.is_bonus ? 'bg-amber-50/40' : 'hover:bg-slate-50'}`} data-testid={`customer-txn-${t.id}`}>
              <td className="px-3 py-2 text-xs whitespace-nowrap font-mono">{t.created_at ? new Date(t.created_at).toLocaleString() : '—'}</td>
              <td className="px-3 py-2 text-xs">
                <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-[10px] font-medium">{t.type}</span>
                {t.is_bonus && <span className="ml-1 px-2 py-0.5 bg-amber-200 text-amber-800 rounded text-[10px] font-bold">BONUS</span>}
              </td>
              <td className="px-3 py-2 text-xs">
                {t.order_id ? (
                  <button
                    type="button"
                    onClick={() => onOpenOrder && onOpenOrder(t.order_id)}
                    data-testid={`customer-txn-open-order-${t.id}`}
                    title="Open this order"
                    className="font-mono text-cyan-700 hover:text-cyan-900 hover:underline font-semibold"
                  >
                    {t.order_number || `#${String(t.order_id).slice(0, 8)}`}
                  </button>
                ) : (
                  <span className="text-slate-300">—</span>
                )}
              </td>
              <td className={`px-3 py-2 text-xs ${t.direction === 'to_user' ? 'text-emerald-600' : 'text-rose-600'}`}>{t.direction}</td>
              <td className={`px-3 py-2 text-right font-bold ${t.direction === 'to_user' ? (t.is_bonus ? 'text-amber-600' : 'text-emerald-600') : 'text-rose-600'}`}>
                {t.direction === 'to_user' ? '+' : '−'}€{Number(t.amount || 0).toFixed(2)}
              </td>
              <td className="px-3 py-2 text-xs">{t.status}</td>
              <td className="px-3 py-2 text-xs">
                {extTxn ? (
                  <button
                    type="button"
                    onClick={() => {
                      try {
                        navigator.clipboard.writeText(extTxn);
                        if (typeof window !== 'undefined' && window.__toast) window.__toast(`${extKind} TXN copied`);
                      } catch (_) { /* clipboard denied — ignore */ }
                    }}
                    data-testid={`customer-txn-copy-ext-${t.id}`}
                    title={`Copy ${extKind} transaction ID`}
                    className="font-mono text-cyan-700 hover:bg-cyan-50 px-2 py-0.5 rounded border border-cyan-200 inline-flex items-center gap-1"
                  >
                    <span className="text-[9px] font-bold uppercase text-cyan-500">{extKind}</span>
                    <span className="truncate max-w-[140px]">{extTxn}</span>
                  </button>
                ) : (
                  <span className="text-slate-300">—</span>
                )}
              </td>
              <td className="px-3 py-2 text-xs text-slate-600 max-w-[420px] truncate" title={t.description}>{t.description}</td>
              <td className="px-3 py-2 text-right">
                {t.type === 'admin_adjustment' && (t.manual_kind === 'credit' || t.manual_kind === 'debit' || !!t.is_bonus) && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); deleteAdjustment(t); }}
                    disabled={deletingId === t.id}
                    title="Delete adjustment & roll back balance"
                    data-testid={`customer-txn-delete-${t.id}`}
                    className="p-1.5 text-rose-600 hover:bg-rose-50 rounded inline-flex items-center justify-center disabled:opacity-50"
                  >
                    {deletingId === t.id
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Trash2 className="w-4 h-4" />}
                  </button>
                )}
              </td>
            </tr>
              );
            })}
        </tbody>
      </table>
      </div>
    </div>
  </div>
  );
};

const Section = ({ title, icon: Icon, accent = 'cyan', children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2.5">
      <span className={`w-7 h-7 rounded-lg bg-gradient-to-br from-${accent}-500 to-${accent}-600 flex items-center justify-center`}>
        <Icon className="w-3.5 h-3.5 text-white" />
      </span>
      {title}
    </h3>
    {children}
  </div>
);

const Stat = ({ label, value, icon: Icon, color, testid }) => (
  <div className="bg-white p-3 rounded-lg border border-slate-200 flex items-center gap-3" data-testid={testid}>
    <div className={`w-9 h-9 rounded-lg bg-${color}-50 flex items-center justify-center`}>
      <Icon className={`w-4 h-4 text-${color}-600`} />
    </div>
    <div className="min-w-0">
      <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wide">{label}</p>
      <p className="text-sm font-bold text-slate-800 truncate">{value}</p>
    </div>
  </div>
);

const LegalRow = ({ label, value, optional }) => (
  <div className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
    {value
      ? <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
      : (optional
          ? <AlertTriangle className="w-4 h-4 text-slate-400 flex-shrink-0" />
          : <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />)}
    <span className="text-sm text-slate-700">{label}{optional && <span className="text-xs text-slate-400 ml-1">(opt)</span>}</span>
    <span className="ml-auto text-xs font-semibold">
      {value
        ? <span className="text-emerald-600">accepted</span>
        : <span className="text-slate-400">{optional ? 'not subscribed' : 'NOT accepted'}</span>}
    </span>
  </div>
);
